import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';

const EMPTY_PROFILE_FORM = {
  id: null,
  channelPattern: '',
  automationMode: '',
  maxAmount: '',
  maxCreditRisk: '',
  minContracts: '',
  tradeAccountId: '',
  paperTradingEnabled: '',
  liveTradingEnabled: '',
  enabled: true
};

/**
 * Per-channel automation overrides for a token. A profile's non-empty fields
 * override the token defaults when an alert's source/channel matches the
 * pattern (case-insensitive substring).
 */
const ChannelProfilesSection = ({ tokenId, accounts, onError }) => {
  const [profiles, setProfiles] = useState([]);
  const [pForm, setPForm] = useState(EMPTY_PROFILE_FORM);
  const [pSaving, setPSaving] = useState(false);

  const fetchProfiles = async () => {
    if (!tokenId) return;
    try {
      const res = await apiRequest(`/api/tokens/${tokenId}/profiles`);
      if (res.status === 200 && res.data?.success) setProfiles(res.data.data || []);
    } catch (e) { /* table may not exist yet — profiles stay empty */ }
  };

  useEffect(() => {
    setProfiles([]);
    setPForm(EMPTY_PROFILE_FORM);
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenId]);

  const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : v;

  const saveProfile = async () => {
    if (!pForm.channelPattern.trim()) return;
    setPSaving(true);
    const payload = {
      channelPattern: pForm.channelPattern.trim(),
      automationMode: pForm.automationMode || null,
      maxAmount: numOrNull(pForm.maxAmount),
      maxCreditRisk: numOrNull(pForm.maxCreditRisk),
      minContracts: numOrNull(pForm.minContracts),
      tradeAccountId: numOrNull(pForm.tradeAccountId),
      paperTradingEnabled: pForm.paperTradingEnabled === '' ? null : pForm.paperTradingEnabled,
      liveTradingEnabled: pForm.liveTradingEnabled === '' ? null : pForm.liveTradingEnabled,
      enabled: pForm.enabled
    };
    try {
      const isEdit = !!pForm.id;
      const res = await apiRequest(
        isEdit ? `/api/tokens/profiles/${pForm.id}` : `/api/tokens/${tokenId}/profiles`,
        { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(payload) }
      );
      if (res.data?.success) {
        setPForm(EMPTY_PROFILE_FORM);
        fetchProfiles();
      } else {
        onError?.(res.data?.error || 'Failed to save channel profile');
      }
    } catch (e) {
      onError?.(e.response?.data?.error || 'Failed to save channel profile');
    } finally {
      setPSaving(false);
    }
  };

  const deleteProfile = async (id) => {
    try {
      const res = await apiRequest(`/api/tokens/profiles/${id}`, { method: 'DELETE' });
      if (res.data?.success) fetchProfiles();
      else onError?.(res.data?.error || 'Failed to delete channel profile');
    } catch (e) {
      onError?.(e.response?.data?.error || 'Failed to delete channel profile');
    }
  };

  const startEdit = (p) => setPForm({
    id: p.id,
    channelPattern: p.channelPattern || '',
    automationMode: p.automationMode || '',
    maxAmount: p.maxAmount ?? '',
    maxCreditRisk: p.maxCreditRisk ?? '',
    minContracts: p.minContracts ?? '',
    tradeAccountId: p.tradeAccountId ?? '',
    paperTradingEnabled: p.paperTradingEnabled ?? '',
    liveTradingEnabled: p.liveTradingEnabled ?? '',
    enabled: p.enabled !== false
  });

  const profileSummary = (p) => {
    const parts = [];
    if (p.automationMode) parts.push(`mode: ${p.automationMode}`);
    if (p.maxCreditRisk != null) parts.push(`credit risk: $${p.maxCreditRisk}`);
    if (p.maxAmount != null) parts.push(`max: $${p.maxAmount}`);
    if (p.minContracts != null) parts.push(`min: ${p.minContracts}`);
    if (p.tradeAccountId != null) {
      const acc = accounts.find(a => a.id === p.tradeAccountId);
      parts.push(`acct: ${acc ? (acc.name || `#${acc.id}`) : `#${p.tradeAccountId}`}`);
    }
    if (p.paperTradingEnabled === true) parts.push('paper');
    if (p.liveTradingEnabled === true) parts.push('live');
    return parts.length ? parts.join(' · ') : 'inherits token defaults';
  };

  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" gutterBottom>
        Channel Profiles
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Override automation settings per alert channel. The first enabled profile whose pattern
        appears in the alert's source/channel name wins; empty fields inherit the token defaults.
      </Typography>

      {profiles.map((p) => (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Switch
            size="small"
            checked={p.enabled !== false}
            onChange={async () => {
              await apiRequest(`/api/tokens/profiles/${p.id}`, {
                method: 'PUT',
                body: JSON.stringify({ enabled: !p.enabled })
              });
              fetchProfiles();
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.channelPattern}</Typography>
            <Typography variant="caption" color="text.secondary">{profileSummary(p)}</Typography>
          </Box>
          <IconButton size="small" onClick={() => startEdit(p)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => deleteProfile(p.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <TextField
          size="small"
          label="Channel Pattern"
          fullWidth
          value={pForm.channelPattern}
          onChange={(e) => setPForm({ ...pForm, channelPattern: e.target.value })}
          helperText="e.g. live-alerts — matched against the alert source/channel name"
        />
        <TextField
          size="small"
          label="Automation Mode"
          select
          sx={{ minWidth: 140 }}
          value={pForm.automationMode}
          onChange={(e) => setPForm({ ...pForm, automationMode: e.target.value })}
        >
          <MenuItem value="">Inherit</MenuItem>
          <MenuItem value="opening">Opening Only</MenuItem>
          <MenuItem value="closing">Closing Only</MenuItem>
          <MenuItem value="both">Both</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <TextField
          size="small"
          label="Max Credit Risk ($)"
          type="number"
          fullWidth
          value={pForm.maxCreditRisk}
          onChange={(e) => setPForm({ ...pForm, maxCreditRisk: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Max Amount ($)"
          type="number"
          fullWidth
          value={pForm.maxAmount}
          onChange={(e) => setPForm({ ...pForm, maxAmount: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Min Contracts"
          type="number"
          fullWidth
          value={pForm.minContracts}
          onChange={(e) => setPForm({ ...pForm, minContracts: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Trade Account"
          select
          fullWidth
          value={pForm.tradeAccountId}
          onChange={(e) => setPForm({ ...pForm, tradeAccountId: e.target.value })}
        >
          <MenuItem value="">Inherit</MenuItem>
          {accounts.map((acc) => {
            const type = acc.apiType || acc.api_type || '';
            const typeLabel = type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            return (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.name || acc.account_name || `Account ${acc.id}`} ({typeLabel})
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          size="small"
          label="Paper Trading"
          select
          sx={{ minWidth: 120 }}
          value={pForm.paperTradingEnabled}
          onChange={(e) => setPForm({ ...pForm, paperTradingEnabled: e.target.value })}
        >
          <MenuItem value="">Inherit</MenuItem>
          <MenuItem value={true}>On</MenuItem>
          <MenuItem value={false}>Off</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Live Trading"
          select
          sx={{ minWidth: 120 }}
          value={pForm.liveTradingEnabled}
          onChange={(e) => setPForm({ ...pForm, liveTradingEnabled: e.target.value })}
        >
          <MenuItem value="">Inherit</MenuItem>
          <MenuItem value={true}>On</MenuItem>
          <MenuItem value={false}>Off</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        {pForm.id && (
          <Button size="small" onClick={() => setPForm(EMPTY_PROFILE_FORM)}>Cancel Edit</Button>
        )}
        <Button
          size="small"
          variant="contained"
          onClick={saveProfile}
          disabled={pSaving || !pForm.channelPattern.trim()}
        >
          {pForm.id ? 'Update Profile' : 'Add Profile'}
        </Button>
      </Box>
    </Box>
  );
};

const ApiTokens = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [alertType, setAlertType] = useState(''); // '', 'telegram', 'discord', 'webhook'

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    channelId: '',
    chatId: '',
    expiresAt: '',
    rateLimitPerMinute: 60,
    automationEnabled: false,
    minContracts: '',
    lottoMinContracts: '',
    maxAmount: '',
    maxCreditRisk: '',
    lottoMaxAmount: '',
    lottoMaxCreditRisk: '',
    orderType: 'market',
    automationMode: 'both',
    useQuantityFraction: false,
    tradeAccountId: '',
    allowHeroTrades: false,
    paperTradingEnabled: false,
    liveTradingEnabled: false,
    takeProfitPct: '',
    stopLossPct: '',
    priceGuardThreshold: 10
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    channelId: '',
    chatId: '',
    isActive: true,
    rateLimitPerMinute: 60,
    automationEnabled: false,
    minContracts: '',
    lottoMinContracts: '',
    maxAmount: '',
    maxCreditRisk: '',
    lottoMaxAmount: '',
    lottoMaxCreditRisk: '',
    orderType: 'market',
    automationMode: 'both',
    useQuantityFraction: false,
    tradeAccountId: '',
    allowHeroTrades: false,
    paperTradingEnabled: false,
    liveTradingEnabled: false,
    takeProfitPct: '',
    stopLossPct: '',
    priceGuardThreshold: 10
  });

  useEffect(() => {
    fetchTokens();
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await apiRequest('/api/accounts');
      if (response.status === 200 && response.data) {
        const allAccounts = Array.isArray(response.data) ? response.data : (response.data.data || response.data || []);
        const brokerAccounts = allAccounts.filter(a => {
          const type = (a.apiType || a.api_type || '').toLowerCase();
          return ['schwab', 'robinhood', 'robinhood-mcp', 'tradier', 'tradier-mcp', 'manual'].includes(type);
        });
        setAccounts(brokerAccounts);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchTokens = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/tokens');
      if (response.status === 200 && response.data.success) {
        setTokens(response.data.data);
      } else {
        setError('Failed to fetch tokens');
      }
    } catch (err) {
      setError('Error fetching tokens');
      console.error('Error fetching tokens:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    try {
      const response = await apiRequest('/api/tokens', {
        method: 'POST',
        body: JSON.stringify({ ...formData, alertType })
      });

      if (response.status === 201 && response.data.success) {
        if (response.data.data.token) {
          setNewToken(response.data.data.token);
        } else {
          setSuccessMsg('Alert configuration created successfully.');
          setTimeout(() => setSuccessMsg(null), 3000);
        }
        setDialogOpen(false);
        setFormData({ name: '', channelId: '', chatId: '', expiresAt: '', rateLimitPerMinute: 60, automationEnabled: false, minContracts: '', lottoMinContracts: '', maxAmount: '', maxCreditRisk: '', lottoMaxAmount: '', lottoMaxCreditRisk: '', orderType: 'market', automationMode: 'both', useQuantityFraction: false, tradeAccountId: '', allowHeroTrades: false, paperTradingEnabled: false, liveTradingEnabled: false, takeProfitPct: '', stopLossPct: '', priceGuardThreshold: 10 });
        fetchTokens();
      } else {
        setError(response.data?.error || 'Failed to create alert');
      }
    } catch (err) {
      setError('Error creating alert');
      console.error('Error creating alert:', err);
    }
  };

  const handleUpdateToken = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await apiRequest(`/api/tokens/${selectedToken.id}`, {
        method: 'PUT',
        body: JSON.stringify(editFormData)
      });

      if (response.data?.success) {
        setEditDialogOpen(false);
        setSelectedToken(null);
        setSuccessMsg('Alert config updated successfully');
        setTimeout(() => setSuccessMsg(null), 3000);
        fetchTokens();
      } else {
        setError(response.data?.error || 'Failed to update token');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating token');
      console.error('Error updating token:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteToken = async () => {
    try {
      const response = await apiRequest(`/api/tokens/${selectedToken.id}`, {
        method: 'DELETE'
      });

      if (response.status === 200 && response.data.success) {
        setDeleteDialogOpen(false);
        setSelectedToken(null);
        fetchTokens();
      } else {
        setError(response.data?.error || 'Failed to delete token');
      }
    } catch (err) {
      setError('Error deleting token');
      console.error('Error deleting token:', err);
    }
  };

  const openCreateDialog = () => {
    setAlertType('');
    setFormData({
      name: '',
      channelId: '',
      chatId: '',
      expiresAt: '',
      rateLimitPerMinute: 60,
      automationEnabled: false,
      minContracts: '',
      lottoMinContracts: '',
      maxAmount: '',
      maxCreditRisk: '',
      lottoMaxAmount: '',
      lottoMaxCreditRisk: '',
      orderType: 'market',
      automationMode: 'both',
      useQuantityFraction: false,
      tradeAccountId: '',
      paperTradingEnabled: false,
      liveTradingEnabled: false,
      takeProfitPct: '',
      stopLossPct: '',
      priceGuardThreshold: 10
    });
    setDialogOpen(true);
  };

  const openEditDialog = (token) => {
    setEditFormData({
      name: token.name,
      channelId: token.channelId || '',
      chatId: token.chatId || '',
      isActive: token.isActive,
      rateLimitPerMinute: token.rateLimitPerMinute,
      automationEnabled: token.automationEnabled || false,
      minContracts: token.minContracts || '',
      lottoMinContracts: token.lottoMinContracts || '',
      maxAmount: token.maxAmount || '',
      maxCreditRisk: token.maxCreditRisk || '',
      lottoMaxAmount: token.lottoMaxAmount || '',
      lottoMaxCreditRisk: token.lottoMaxCreditRisk || '',
      orderType: token.orderType || 'market',
      automationMode: token.automationMode || 'both',
      useQuantityFraction: token.useQuantityFraction || false,
      tradeAccountId: token.tradeAccountId || '',
      allowHeroTrades: token.allowHeroTrades || false,
      paperTradingEnabled: token.paperTradingEnabled || false,
      liveTradingEnabled: token.liveTradingEnabled || false,
      takeProfitPct: token.takeProfitPct != null ? token.takeProfitPct : '',
      stopLossPct: token.stopLossPct != null ? token.stopLossPct : '',
      priceGuardThreshold: token.priceGuardThreshold != null ? token.priceGuardThreshold : 10
    });
    setSelectedToken(token);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (token) => {
    setSelectedToken(token);
    setDeleteDialogOpen(true);
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, sm: 3 } }}>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your alert configurations. Alerts can come from Telegram, Discord, or Webhook sources.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {newToken && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNewToken(null)}>
          <Typography variant="body2" gutterBottom>
            Token created successfully! Save this token securely as it will not be shown again.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <TextField
              value={showToken ? newToken : '•'.repeat(64)}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                        {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton onClick={() => copyToken(newToken)} edge="end">
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add New Alert
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Token (Last 4)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Rate Limit</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No alerts found. Add a new alert to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tokens.map((token) => (
                      <TableRow key={token.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {token.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(token.alertType || 'webhook').charAt(0).toUpperCase() + (token.alertType || 'webhook').slice(1)}
                            size="small"
                            color={token.alertType === 'webhook' ? 'primary' : token.alertType === 'telegram' ? 'info' : 'secondary'}
                            variant={token.alertType === 'webhook' ? 'filled' : 'outlined'}
                          />
                          {(token.channelId || token.chatId) && (
                            <Typography variant="caption" display="block" fontFamily="monospace" color="text.secondary" sx={{ mt: 0.5 }}>
                              {token.alertType === 'discord' ? 'ch:' : 'chat:'}{token.channelId || token.chatId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            ••••{token.lastFour}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={token.isActive ? 'Active' : 'Inactive'}
                            color={token.isActive ? 'success' : 'default'}
                            size="small"
                          />
                          {isExpired(token.expiresAt) && (
                            <Chip
                              label="Expired"
                              color="error"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                          {token.paperTradingEnabled && (
                            <Chip
                              label="Paper"
                              color="warning"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                          {token.liveTradingEnabled && (
                            <Chip
                              label="Live"
                              color="error"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {token.rateLimitPerMinute}/min
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(token.expiresAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(token.lastUsedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(token.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(token)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(token)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Create Token Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Alert</DialogTitle>
        <DialogContent>
          <TextField
            select
            autoFocus
            margin="dense"
            label="Alert Source"
            fullWidth
            variant="outlined"
            value={alertType}
            onChange={(e) => setAlertType(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select alert source…</MenuItem>
            <MenuItem value="telegram">Telegram (Telegram Listener)</MenuItem>
            <MenuItem value="discord">Discord (Discord Bot)</MenuItem>
            <MenuItem value="webhook">Webhook</MenuItem>
          </TextField>

          {alertType === 'telegram' && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This alert will use your Telegram Listener. Make sure your Telegram API credentials are configured in <strong>User Profile {'>'} Bot Configuration</strong>.
              </Alert>
              <TextField
                margin="dense"
                label="Telegram Chat ID"
                fullWidth
                variant="outlined"
                required
                value={formData.chatId}
                onChange={(e) => setFormData({ ...formData, chatId: e.target.value })}
                helperText="The chat (or channel) ID this alert config listens to — each config covers a single chat"
                sx={{ mb: 2 }}
              />
            </>
          )}

          {alertType === 'discord' && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This alert will use your Discord Bot. Make sure your Discord bot token is configured in <strong>User Profile {'>'} Bot Configuration</strong>.
              </Alert>
              <TextField
                margin="dense"
                label="Discord Channel ID"
                fullWidth
                variant="outlined"
                required
                value={formData.channelId}
                onChange={(e) => setFormData({ ...formData, channelId: e.target.value })}
                helperText="The channel this alert config listens to — create one config per channel (right-click a channel in Discord with Developer Mode on to copy its ID)"
                sx={{ mb: 2 }}
              />
            </>
          )}

          {alertType && (
            <>
          <TextField
            margin="dense"
            label="Token Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Expiration Date (Optional)"
            type="datetime-local"
            fullWidth
            variant="outlined"
            InputLabelProps={{
              shrink: true
            }}
            value={formData.expiresAt}
            onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Rate Limit (requests per minute)"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.rateLimitPerMinute}
            onChange={(e) => setFormData({ ...formData, rateLimitPerMinute: parseInt(e.target.value) || 60 })}
            inputProps={{ min: 1, max: 1000 }}
            helperText="Between 1 and 1000 requests per minute"
            sx={{ mb: 2 }}
          />
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Automation Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.automationEnabled}
                onChange={(e) => setFormData({ ...formData, automationEnabled: e.target.checked })}
              />
            }
            label="Enable Automated Trading"
            sx={{ mb: 2, display: 'block' }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Min Contracts"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.minContracts}
              onChange={(e) => setFormData({ ...formData, minContracts: parseInt(e.target.value) || '' })}
              inputProps={{ min: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText="Starting size per alert"
            />
            <TextField
              margin="dense"
              label="Max Amount ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.maxAmount}
              onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Debit/premium budget — grows size to largest count under it"
            />
            <TextField
              margin="dense"
              label="Max Credit Risk ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.maxCreditRisk}
              onChange={(e) => setFormData({ ...formData, maxCreditRisk: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Max $ at risk per credit spread/condor entry — sizes on (width − credit); required for credit entries"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Lotto Min Contracts"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.lottoMinContracts}
              onChange={(e) => setFormData({ ...formData, lottoMinContracts: parseInt(e.target.value) || '' })}
              inputProps={{ min: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText="Starting size for lotto / high-risk alerts"
            />
            <TextField
              margin="dense"
              label="Lotto Max Amount ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.lottoMaxAmount}
              onChange={(e) => setFormData({ ...formData, lottoMaxAmount: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Optional $ cap for lotto alerts (falls back to Max Amount)"
            />
            <TextField
              margin="dense"
              label="Lotto Max Credit Risk ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.lottoMaxCreditRisk}
              onChange={(e) => setFormData({ ...formData, lottoMaxCreditRisk: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Max $ at risk for lotto credit spreads (falls back to Max Credit Risk)"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Order Type"
              select
              fullWidth
              variant="outlined"
              value={formData.orderType}
              onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
            >
              <MenuItem value="market">Market Order</MenuItem>
              <MenuItem value="limit">Limit Order (ignores price)</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              label="Price Guard %"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.priceGuardThreshold}
              onChange={(e) => setFormData({ ...formData, priceGuardThreshold: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Skip live BUY if mid exceeds alert by this % (default 10%)"
            />
          </Box>
          <TextField
            margin="dense"
            label="Automation Mode"
            select
            fullWidth
            variant="outlined"
            value={formData.automationMode}
            onChange={(e) => setFormData({ ...formData, automationMode: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="opening">Opening Only</MenuItem>
            <MenuItem value="closing">Closing Only</MenuItem>
            <MenuItem value="both">Both Opening and Closing</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={formData.useQuantityFraction}
                onChange={(e) => setFormData({ ...formData, useQuantityFraction: e.target.checked })}
              />
            }
            label="Use Quantity Fraction for Closing Alerts"
            helperText="When enabled, use the fraction (e.g., 1/4) from closing alerts to determine position size"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowHeroTrades}
                onChange={(e) => setFormData({ ...formData, allowHeroTrades: e.target.checked })}
              />
            }
            label="Allow Auto-Execution of Hero / Zero / High-Risk Trades"
            helperText="When disabled, alerts containing hero/zero/high-risk phrases are held for manual order entry review"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.liveTradingEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormData({
                    ...formData,
                    liveTradingEnabled: enabled,
                    tradeAccountId: enabled ? formData.tradeAccountId : ''
                  });
                }}
                disabled={!formData.automationEnabled}
              />
            }
            label="Live Trading Mode"
            helperText="When enabled, real broker orders are placed for automated alerts"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.paperTradingEnabled}
                onChange={(e) => setFormData({ ...formData, paperTradingEnabled: e.target.checked })}
                disabled={!formData.automationEnabled}
              />
            }
            label="Paper Trading Mode"
            helperText="When enabled, alerts are simulated as paper trades using live Schwab quotes — no real orders are placed"
            sx={{ mb: 2, display: 'block' }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Paper Take-Profit %"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.takeProfitPct}
              onChange={(e) => setFormData({ ...formData, takeProfitPct: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Auto-exit % gain for paper trades"
            />
            <TextField
              margin="dense"
              label="Paper Stop-Loss %"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.stopLossPct}
              onChange={(e) => setFormData({ ...formData, stopLossPct: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Auto-exit % loss for paper trades"
            />
          </Box>
          <TextField
            select
            margin="dense"
            label="Trade Account"
            fullWidth
            variant="outlined"
            value={formData.tradeAccountId}
            onChange={(e) => setFormData({ ...formData, tradeAccountId: e.target.value ? parseInt(e.target.value) : '' })}
            helperText="Broker account for live automated trades (requires Live Trading)"
            disabled={!formData.liveTradingEnabled}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Auto (first available)</MenuItem>
            {accounts.map((acc) => {
              const type = acc.apiType || acc.api_type || '';
              const typeLabel = type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              return (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name || acc.account_name || `Account ${acc.id}`} ({typeLabel})
                </MenuItem>
              );
            })}
          </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {alertType && (
            <Button onClick={handleCreateToken} variant="contained" disabled={!formData.name}>
              {alertType === 'webhook' ? 'Create Token' : 'Create Alert'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Token Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Alert Config</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Token Name"
            fullWidth
            variant="outlined"
            value={editFormData.name}
            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          {selectedToken?.alertType === 'discord' && (
            <TextField
              margin="dense"
              label="Discord Channel ID"
              fullWidth
              variant="outlined"
              required
              value={editFormData.channelId}
              onChange={(e) => setEditFormData({ ...editFormData, channelId: e.target.value })}
              helperText="The Discord channel this config listens to"
              sx={{ mb: 2 }}
            />
          )}
          {selectedToken?.alertType === 'telegram' && (
            <TextField
              margin="dense"
              label="Telegram Chat ID"
              fullWidth
              variant="outlined"
              required
              value={editFormData.chatId}
              onChange={(e) => setEditFormData({ ...editFormData, chatId: e.target.value })}
              helperText="The Telegram chat this config listens to"
              sx={{ mb: 2 }}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.isActive}
                onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mb: 2, display: 'block' }}
          />
          <TextField
            margin="dense"
            label="Rate Limit (requests per minute)"
            type="number"
            fullWidth
            variant="outlined"
            value={editFormData.rateLimitPerMinute}
            onChange={(e) => setEditFormData({ ...editFormData, rateLimitPerMinute: parseInt(e.target.value) || 60 })}
            inputProps={{ min: 1, max: 1000 }}
            helperText="Between 1 and 1000 requests per minute"
            sx={{ mb: 2 }}
          />
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Automation Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.automationEnabled}
                onChange={(e) => setEditFormData({ ...editFormData, automationEnabled: e.target.checked })}
              />
            }
            label="Enable Automated Trading"
            sx={{ mb: 2, display: 'block' }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Min Contracts"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.minContracts}
              onChange={(e) => setEditFormData({ ...editFormData, minContracts: parseInt(e.target.value) || '' })}
              inputProps={{ min: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText="Starting size per alert"
            />
            <TextField
              margin="dense"
              label="Max Amount ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.maxAmount}
              onChange={(e) => setEditFormData({ ...editFormData, maxAmount: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Debit/premium budget — grows size to largest count under it"
            />
            <TextField
              margin="dense"
              label="Max Credit Risk ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.maxCreditRisk}
              onChange={(e) => setEditFormData({ ...editFormData, maxCreditRisk: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Max $ at risk per credit spread/condor entry — sizes on (width − credit); required for credit entries"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Lotto Min Contracts"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.lottoMinContracts}
              onChange={(e) => setEditFormData({ ...editFormData, lottoMinContracts: parseInt(e.target.value) || '' })}
              inputProps={{ min: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText="Starting size for lotto / high-risk alerts"
            />
            <TextField
              margin="dense"
              label="Lotto Max Amount ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.lottoMaxAmount}
              onChange={(e) => setEditFormData({ ...editFormData, lottoMaxAmount: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Optional $ cap for lotto alerts (falls back to Max Amount)"
            />
            <TextField
              margin="dense"
              label="Lotto Max Credit Risk ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.lottoMaxCreditRisk}
              onChange={(e) => setEditFormData({ ...editFormData, lottoMaxCreditRisk: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              helperText="Max $ at risk for lotto credit spreads (falls back to Max Credit Risk)"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Order Type"
              select
              fullWidth
              variant="outlined"
              value={editFormData.orderType}
              onChange={(e) => setEditFormData({ ...editFormData, orderType: e.target.value })}
            >
              <MenuItem value="market">Market Order</MenuItem>
              <MenuItem value="limit">Limit Order (ignores price)</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              label="Price Guard %"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.priceGuardThreshold}
              onChange={(e) => setEditFormData({ ...editFormData, priceGuardThreshold: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Skip live BUY if mid exceeds alert by this % (default 10%)"
            />
          </Box>
          <TextField
            margin="dense"
            label="Automation Mode"
            select
            fullWidth
            variant="outlined"
            value={editFormData.automationMode}
            onChange={(e) => setEditFormData({ ...editFormData, automationMode: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="opening">Opening Only</MenuItem>
            <MenuItem value="closing">Closing Only</MenuItem>
            <MenuItem value="both">Both Opening and Closing</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.useQuantityFraction}
                onChange={(e) => setEditFormData({ ...editFormData, useQuantityFraction: e.target.checked })}
              />
            }
            label="Use Quantity Fraction for Closing Alerts"
            helperText="When enabled, use the fraction (e.g., 1/4) from closing alerts to determine position size"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.allowHeroTrades}
                onChange={(e) => setEditFormData({ ...editFormData, allowHeroTrades: e.target.checked })}
              />
            }
            label="Allow Auto-Execution of Hero / Zero / High-Risk Trades"
            helperText="When disabled, alerts containing hero/zero/high-risk phrases are held for manual order entry review"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.liveTradingEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setEditFormData({
                    ...editFormData,
                    liveTradingEnabled: enabled,
                    tradeAccountId: enabled ? editFormData.tradeAccountId : ''
                  });
                }}
                disabled={!editFormData.automationEnabled}
              />
            }
            label="Live Trading Mode"
            helperText="When enabled, real broker orders are placed for automated alerts"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.paperTradingEnabled}
                onChange={(e) => setEditFormData({ ...editFormData, paperTradingEnabled: e.target.checked })}
                disabled={!editFormData.automationEnabled}
              />
            }
            label="Paper Trading Mode"
            helperText="When enabled, alerts are simulated as paper trades using live Schwab quotes — no real orders are placed"
            sx={{ mb: 2, display: 'block' }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Paper Take-Profit %"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.takeProfitPct}
              onChange={(e) => setEditFormData({ ...editFormData, takeProfitPct: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Auto-exit % gain for paper trades"
            />
            <TextField
              margin="dense"
              label="Paper Stop-Loss %"
              type="number"
              fullWidth
              variant="outlined"
              value={editFormData.stopLossPct}
              onChange={(e) => setEditFormData({ ...editFormData, stopLossPct: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Auto-exit % loss for paper trades"
            />
          </Box>
          <TextField
            select
            margin="dense"
            label="Trade Account"
            fullWidth
            variant="outlined"
            value={editFormData.tradeAccountId}
            onChange={(e) => setEditFormData({ ...editFormData, tradeAccountId: e.target.value ? parseInt(e.target.value) : '' })}
            helperText="Broker account for live automated trades (requires Live Trading)"
            disabled={!editFormData.liveTradingEnabled}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Auto (first available)</MenuItem>
            {accounts.map((acc) => {
              const type = acc.apiType || acc.api_type || '';
              const typeLabel = type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              return (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name || acc.account_name || `Account ${acc.id}`} ({typeLabel})
                </MenuItem>
              );
            })}
          </TextField>
          <ChannelProfilesSection tokenId={selectedToken?.id} accounts={accounts} onError={setError} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleUpdateToken} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Token</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the token "{selectedToken?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteToken} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiTokens;
